import { CATALOG_PAGE_SIZE } from '../adapters/catalog/radio-browser';
import type { CatalogStation } from '../domain/catalog';
import { createStation, fromCatalog } from '../domain/library';
import type { Station } from '../domain/stations';
import type { CatalogPort } from '../ports/catalog';
import type { AppState, SearchState } from './app';
import type { Store } from './index';

/**
 * Iskanje po spletni bazi kot storitev (P2 §2).
 *
 * Enak vzorec kot `store/library.ts`: čista logika je pod tem, tu je le
 * zaporedje »izračunaj → objavi → po potrebi vprašaj vir«. Razlika je, da vir
 * ni shramba, ampak omrežje — in omrežje odgovarja **z zamikom in ne po
 * vrsti**, kar določa skoraj vse odločitve spodaj.
 */

/**
 * Zamik, preden tipkanje postane poizvedba.
 *
 * Vir ima en sam strežnik (T3 §1). Poizvedba na vsako tipko bi pomenila deset
 * poizvedb za eno besedo — od tega devet takih, katerih odgovora nihče ne bo
 * videl. 300 ms je pod pragom, pri katerem se čakanje opazi.
 */
const DEBOUNCE_MS = 300;

/**
 * Koliko časa mine, preden se izbire filtrov po neuspehu poskusijo znova.
 *
 * Ni merjena vrednost. Prenizka bi vir tolkla natanko takrat, ko je pokazal,
 * da je pod pritiskom; previsoka pomeni zaslon brez filtrov, čeprav se je vir
 * medtem vrnil. 30 s je pod pragom, pri katerem bi uporabnik nehal iskati.
 */
const FACETS_RETRY_MS = 30_000;

export interface SearchService {
  setQuery(query: string): void;
  setCountry(code: string | null): void;
  setTag(tag: string | null): void;
  /** Ponovi zadnjo poizvedbo — gumb v stanju »vir ni odgovoril« (P4 §5). */
  retry(): void;
  /** Naslednja stran zadetkov; doda jih na konec seznama. */
  more(): void;
  /** Naloži izbire za oba comboboxa. Enkrat na sejo. */
  loadFacets(): void;

  /**
   * Postaja za predposluh (P2 §2.4) — začasna, **ne** gre v knjižnico.
   *
   * Vrne `Station` s časovnim `id`, ki ga pozna samo `playableStations()`.
   */
  preview(station: CatalogStation): Station;
  clearPreview(): void;

  dispose(): void;
}

export interface SearchDeps {
  port: CatalogPort;
  store: Store<AppState>;
  /** Za teste: determinističen `id` in ura. */
  newId?: () => string;
  now?: () => number;
  debounceMs?: number;
}

export function createSearch(deps: SearchDeps): SearchService {
  const { port, store } = deps;
  const debounceMs = deps.debounceMs ?? DEBOUNCE_MS;
  const now = deps.now ?? (() => Date.now());
  const newId = deps.newId ?? (() => `preview-${now().toString(36)}`);

  let timer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Poizvedba v teku. Prekine se ob vsaki novi — brez tega bi počasnejši
   * odgovor prehitel hitrejšega in zadetki bi pripadali nizu, ki ga v polju
   * ni več. To je isti razred napake kot ovitek prejšnje skladbe (T3 §3),
   * le da ga tu ne bi popravil noben naslednji poll.
   */
  let inFlight: AbortController | null = null;
  let facetsRequested = false;

  function patch(next: Partial<SearchState>): void {
    store.set({ search: { ...store.get().search, ...next } });
  }

  function cancelPending(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    inFlight?.abort();
    inFlight = null;
  }

  /**
   * Ali je poizvedba sploh vredna zahtevka.
   *
   * Prazno polje **z** izbranim filtrom je legitimno iskanje: »najbolj
   * poslušane v Sloveniji« (P2 §2.3). Prazno polje brez filtra pa ni — to je
   * prvi obisk zaslona in odgovor nanj je prazno stanje, ne 30 naključnih
   * postaj s cele zemeljske oble.
   */
  function isSearchable(state: SearchState): boolean {
    return state.query.trim() !== '' || state.countryCode !== null || state.tag !== null;
  }

  async function run(offset: number): Promise<void> {
    const search = store.get().search;

    if (!isSearchable(search)) {
      patch({ status: 'idle', results: [], source: null, failure: null, exhausted: true });
      return;
    }

    const controller = new AbortController();
    inFlight = controller;
    patch({ status: 'loading', failure: null });

    const result = await port.search(
      {
        q: search.query,
        countryCode: search.countryCode,
        tag: search.tag,
        limit: CATALOG_PAGE_SIZE,
        offset,
      },
      controller.signal,
    );

    // Med čakanjem je uporabnik tipkal naprej ali zamenjal filter. Ta odgovor
    // pripada poizvedbi, ki je ni več, in zapis bi povozil pravega.
    if (inFlight !== controller) return;
    inFlight = null;

    if (!result.ok) {
      // Prekinjena poizvedba ni izpad: `cancelPending()` jo je ubil namerno in
      // za njo že teče nova. Vsaka tipka bi sicer pobliskala sporočilo o napaki.
      if (controller.signal.aborted) return;
      patch({ status: 'failed', failure: result.reason });
      return;
    }

    const previous = offset === 0 ? [] : store.get().search.results;
    patch({
      status: 'ready',
      failure: null,
      results: [...previous, ...result.stations],
      source: result.source,
      exhausted: result.exhausted,
    });
  }

  function schedule(): void {
    cancelPending();
    timer = setTimeout(() => {
      timer = null;
      void run(0);
    }, debounceMs);
  }

  return {
    setQuery(query) {
      patch({ query });
      schedule();
    },

    setCountry(code) {
      patch({ countryCode: code });
      // Filter je klik in ne tipkanje: čakati 300 ms po njem bi bilo videti
      // kot da se ni zgodilo nič.
      cancelPending();
      void run(0);
    },

    setTag(tag) {
      patch({ tag });
      cancelPending();
      void run(0);
    },

    retry() {
      cancelPending();
      void run(0);
    },

    more() {
      const search = store.get().search;
      if (search.status === 'loading' || search.exhausted) return;
      cancelPending();
      void run(search.results.length);
    },

    async loadFacets() {
      /**
       * ⚠️ Zastavica in ne `countries.length`: klicno mesto je `render()`, ki
       * med čakanjem na odgovor steče še desetkrat. Preverjanje seznama bi
       * zdržalo do prvega uspeha, do takrat pa sprožilo toliko poizvedb,
       * kolikor je bilo izrisov.
       *
       * 🔴 **Neuspeh se NE zaklene za vso sejo.** Prva izvedba je zastavico
       * postavila in je nikoli ni spustila; izmerjeno na produkciji takoj po
       * objavi, ko je vir za nekaj minut vračal 503 (en sam strežnik, T3 §1).
       * Posledica bi bila zaslon **brez obeh spustnih seznamov** do ponovnega
       * zagona aplikacije — in nič ne bi kazalo, da manjkata.
       *
       * Ponovni poskus je omejen s časom in ne s številom izrisov: brez tega
       * bi vsaka tipka med tipkanjem sprožila dva zahtevka na vir, ki je ravno
       * pokazal, da je pod pritiskom.
       */
      if (facetsRequested) return;
      facetsRequested = true;

      const [countries, tags] = await Promise.all([port.facets('countries'), port.facets('tags')]);

      if (countries.length === 0 && tags.length === 0) {
        setTimeout(() => {
          facetsRequested = false;
        }, FACETS_RETRY_MS);
        return;
      }

      patch({ countries, tags });
    },

    preview(station) {
      /**
       * Predposluh je postaja **brez knjižnice**: nima mesta v seznamu, ne
       * gre v shrambo in je ⏮ ⏭ ne obiščeta (`transportOrder` ostane vidni
       * seznam). Zgrajena je z istim `createStation()` kot prava, ker mora
       * skoznjo delovati vse ostalo — pravilo proxyja, sonda sheme, kaskada
       * metapodatkov in medijska seja.
       */
      const preview = createStation(fromCatalog(station), {
        id: newId(),
        now: now(),
        order: -1,
      });
      patch({ preview });
      return preview;
    },

    clearPreview() {
      patch({ preview: null });
    },

    dispose: cancelPending,
  };
}
